import React, {useState, useRef} from 'react'
import styled from 'styled-components'
import {useNavigate} from 'react-router-dom'
import {Responsive, WidthProvider} from 'react-grid-layout'
import SimpleCard from './SimpleCard'
import GraphicCard from './GraphicCard'
import {cards} from 'utils/helpers'
import route from 'utils/enums/route'

const Card = styled.div`
    height: 100%;
    box-shadow: 0 1px 3px #eaf3f5, 0 1px 2px #eaf3f5;
    background-color: #fff;
    width: 100%;
    display: flex;
    flex-direction: ${props => props.short ? 'row' : 'column'};
    padding: ${props => props.small || props.short ? '10px' : '20px'};
    box-sizing: border-box;
    margin-bottom: 20px;
    cursor: pointer;
    overflow: hidden;
    font-size: 16px;
    border-radius: 10px;
    transition: box-shadow .25s, transform .25s cubic-bezier(.55, 0, .1, 1);

    :hover {
        transform: translate3d(0, -1px, 0);
        box-shadow: 0 10px 20px rgba(179, 216, 225, .35), 0 1px 2px rgba(179, 216, 225, .35);

        + span {
            opacity: 1
        }

        > svg {
            opacity: 1
        }
    }

    + span {
        opacity: 0
    }

    + span:hover {
        opacity: 1
    }

    > svg {
        opacity: 0
    }
`

const Icon = styled.div`
    position: absolute;
    background: rgba(255, 255, 255, 0.5);
    height: 50px;
    width: 50px;
    padding: 5px;
    border-radius: 5px;
    z-index: 1111;
    pointer-events: none;
    display: none;

    span {
        height: 100%;
        width: 100%;
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        display: block;
    }
`

const ResponsiveGridLayout = WidthProvider(Responsive)

const Grid = ({mobile, layoutCards, layoutCfg, editable, rollups, isDesigner, updatingCard,
	getRollup, handleCopy, editDialogOpen, handleChangeLayout, handleChangeModule}) => {

	let navigate = useNavigate()	
	const icon = useRef(null)
	const [id, setId] = useState(null)
	const dragging = {
		x: null,
        y: null,
        ts: null,
        container: null,
		draggable: null
	}

    const hasCardOnPosition = (x, y, layout) => {
        for (let i = 0; i < layout.length; i++) {
            const cfg = layout[i]
            if (cfg.y - 1 <= y && cfg.y - 1 + cfg.height > y) {
                if (cfg.x - 1 <= x && cfg.x - 1 + cfg.width > x) {
                    return true
                }
            }
        }

        return false
    }

    const getAllCoordinates = (baseX, baseY, width, height) => {
        const result = []
        for (let y = baseY; y < (baseY + height); y++) {
            for (let x = baseX; x < (baseX + width); x++) {
                result.push({ x, y })
            }
        }

        return result
    }

    const getPosition = (item, layoutCfg) => {
        const ColsNumber = 6
        let y = 0
        while (true) {
            for (let x = 0; x < ColsNumber - (item.width - 1); x++) {
                const valid = getAllCoordinates(x, y, item.width, item.height).map((c) => {
                    return !hasCardOnPosition(c.x, c.y, layoutCfg)
                }).reduce((a, c) => a && c)
                if (valid) {
                    return { x, y }
                }
            }

            y++
        }
    }

    const getConfig = (items, layoutCfg) => {
        const layout = [...layoutCfg]
        return items.map((item) => {
            let config = layoutCfg.find(config => item.id === config.id)

            if (!config) {
                config = {
                    width: item.type === cards.Graphic ? 2 : 1,
                    height: item.type === cards.Graphic ? 3 : 2
                }

                const pos = getPosition(config, layout)
                config.x = pos.x + 1
                config.y = pos.y + 1
                layout.push(config)
            }

            return {
                i: item.id.toString(),
                x: config.x - 1,
                y: config.y - 1,
                w: config.width,
                h: config.height,
                minW: !mobile ? item.type === cards.Graphic ? 2 : 1 : 0,
                minH: !mobile ? item.type === cards.Graphic ? 3 : 1 : 0,
                name: item.label || item.singular,
                ts: item.lastEdited,
                listId: item.listId,
                planningId: item.planningId,
                schedulerId: item.schedulerId,
				tableId: !item.type ? item.id : null,
                icon: item.icon,
                type: item.type,
                rollup: item.rollup,
                options: item.options,
                hideLabel: item.hideLabel
            }
        })
    }

    const onItemDragStart = (layout, oldItem, newItem, placeholder, e) => {
        const conatiner = document.querySelector('.react-grid-layout')
        conatiner.classList.add('grid-transition')
        dragging.x = e.clientX
        dragging.y = e.clientY
        dragging.ts = (new Date()).getTime()
        const img = layoutCards.find(item => item.id === Number(oldItem.i)).icon
        icon.current.children[0].className = img ? 'entity-icon-' + img : 'entity-icon-graphiccard'
    }

    const onItemDrag = (layout, oldItem, newItem, placeholder, e) => {
        e.stopPropagation()
        if (dragging.draggable && dragging.container) {
            if (e.clientX > dragging.container.x && e.clientX < dragging.container.x + dragging.container.width &&
                e.clientY > dragging.container.y && e.clientY < dragging.container.y + dragging.container.height) {
                    dragging.draggable[0].style.visibility = 'hidden'
                    icon.current.style.display = 'block'
                    icon.current.style.left = e.pageX - icon.current.offsetWidth / 2 + 'px'
                    icon.current.style.top = e.pageY - icon.current.offsetHeight / 2 + 'px'
            } else {
                dragging.draggable[0].style.visibility = 'visible'
                icon.current.style.display = 'none'
            }
        }

        if (dragging.ts && dragging.x && dragging.y) {
            const incX = Math.abs(dragging.x - e.clientX)
            const incY = Math.abs(dragging.y - e.clientY)

            if (((new Date()).getTime() - dragging.ts) > 300 || incX > 10 || incY > 10) {
                dragging.draggable = document.getElementsByClassName('react-draggable-dragging')
                dragging.container = document.getElementById('modules').getBoundingClientRect()
                dragging.draggable[0].classList.add('noneEvents')
                dragging.x = null
                dragging.y = null
                dragging.ts = null
            }
        }
    }

    const onItemDragStop = (layout, oldItem, newItem, placeholder, e) => {
        const el = document.querySelector(`[data-card-id='${newItem.i}']`)
        const conatiner = document.querySelector('.react-grid-layout')
        el.style.visibility = ''

        if (dragging.container && e.clientX > dragging.container.x && e.clientX < dragging.container.x + dragging.container.width &&
            e.clientY > dragging.container.y && e.clientY < dragging.container.y + dragging.container.height) {
                document.querySelectorAll('[data-module-id]').forEach((moduleEl) => {
                    const box = moduleEl.getBoundingClientRect()
                    if (e.clientX > box.x && e.clientX < box.x + box.width &&
                        e.clientY > box.y && e.clientY < box.y + box.height) {
                        el.style.visibility = 'hidden'
                        setId(newItem.i)
                        handleChange(newItem.i, moduleEl.dataset.moduleId)
                    }
                })
        }

        if (id !== newItem.i && (newItem.x !== oldItem.x || newItem.y !== oldItem.y)) {
            const config = layout.map(item => {
                return {
                    x: item.x + 1,
                    y: item.y + 1,
                    id: Number(item.i),
                    height: item.h,
                    width: item.w
                }
            })

            handleChangeLayout(config)
        }

        dragging.x = null
        dragging.y = null
        dragging.ts = null
        dragging.container = null
        icon.current.children[0].className = ''
        icon.current.style.display = ''
        icon.current.style.left = ''
        icon.current.style.top = ''
        conatiner.classList.remove('grid-transition')
    }

    const onResizeStart = () => {
        const conatiner = document.querySelector('.react-grid-layout')
        conatiner.classList.add('grid-transition')
    }

    const onItemResizeStop = (layout, oldItem, newItem, placeholder, e) => {
        const conatiner = document.querySelector('.react-grid-layout')
        conatiner.classList.remove('grid-transition')

        if (newItem.w !== oldItem.w || newItem.h !== oldItem.h) {
            const config = layout.map(item => {
                return {
                    x: item.x + 1,
                    y: item.y + 1,
                    id: Number(item.i),
                    height: item.h,
                    width: item.w
                }
            })

            handleChangeLayout(config)
        }
    }

    const handleChange = (cardId, moduleId) => {
        handleChangeModule(Number(cardId), Number(moduleId))
    }

    const handleClick = (data) => {
        if (data.type === cards.Planning) {
            navigate(`/${route.Planning}/${data.planningId}/month`)
        } else if (data.type === cards.Scheduler) {
            navigate(`/${route.Scheduler}/${data.schedulerId}/month`)
        } else if (data.type === cards.List || data.type === cards.Widget || data.type === cards.Graphic) {
            navigate(`/${route.List}/${data.listId}`)
        } else {
			navigate(`/${route.Table}/${data.tableId}`)
		}
    }

	const layout = getConfig(layoutCards, layoutCfg)
	const layouts = {lg: layout, md: layout, sm: layout, xs: layout, xxs: layout}

	return (
		<React.Fragment>
			<ResponsiveGridLayout
				onResizeStart={onResizeStart}
				onResizeStop={onItemResizeStop}
				onDragStop={onItemDragStop}
				onDragStart={onItemDragStart}
				onDrag={onItemDrag}
				className="layout"
				layouts={layouts}
				rowHeight={100}
				margin={[15, 15]}
				breakpoints={{lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0}}
				cols={mobile ? {lg: 1, md: 1, sm: 1, xs: 1, xxs: 1} : {lg: 6, md: 6, sm: 6, xs: 6, xxs: 1}}
				isDraggable={!mobile && isDesigner && editable}
				isResizable={!mobile && isDesigner && editable}
			>
				{layout.map((item, index) =>
					<div key={item.i} data-card-id={item.i}>
						<Card
							small={item.w === 1 && item.h === 1}
							short={item.h === 1 && item.w > 1}
							onClick={() => handleClick(item)}
						>
							{item.type === cards.Graphic ?
								<GraphicCard key={item.i}
									handleClick={editDialogOpen}
									name={item.name}
									id={Number(item.i)}
									rollup={item.rollup}
									getRollup={getRollup}
									rollupValue={item.rollup && rollups[Number(item.i)]}
									type={item.type}
									options={item.options}
									updating={updatingCard}
									hideLabel={item.hideLabel}
									isDesigner={isDesigner}
									mobile={mobile}
									editable={editable}
								/> :
								<SimpleCard mobile={mobile}
									handleClick={editDialogOpen}
									handleCopy={handleCopy}
									icon={item.icon}
									name={item.name}
									ts={item.ts}
									widget={item.type === cards.Widget}
									planning={item.type === cards.Planning || item.type === cards.Scheduler}
									id={Number(item.i)}
									getRollup={getRollup}
									rollupValue={item.rollup && rollups[Number(item.i)]}
									type={item.type}
									updating={updatingCard}
									small={item.w === 1 && item.h === 1}
									short={item.h === 1 && item.w > 1}
									isDesigner={isDesigner}
									editable={editable}
								/>
							}
						</Card>
					</div>
				)}
			</ResponsiveGridLayout>
			<Icon ref={icon}><span /></Icon>
		</React.Fragment>
	)
}

export default Grid
